import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import CompactLayout from "@/components/layout/CompactLayout";
import CustomersPage from "@/pages/CustomersPage";
import OrdersPage from "@/pages/OrdersPage";
import OrdersDashboardPage from "@/pages/OrdersDashboardPage";
import NewOrderPage from "@/pages/NewOrderPage";
import EditOrderPage from "@/pages/EditOrderPage";
import GarmentsPage from "@/pages/GarmentsPage";
import FabricProcurementPage from "@/pages/FabricProcurementPage";
import ProductionDashboardPage from "@/pages/ProductionDashboardPage";
import SuppliersPage from "@/pages/SuppliersPage";
import MeasurementsPage from "@/pages/MeasurementsPage";
import ProductsPage from "@/pages/ProductsPage";
import PaymentsPage from "@/pages/PaymentsPage";
import TasksPage from "@/pages/TasksPage";
// New placeholder pages
import PatternDetailPage from "@/pages/PatternDetailPage";
import UnifiedFittingStudioPage from "@/pages/UnifiedFittingStudioPage";
import FittingStudioLandingPage from "@/pages/FittingStudioLandingPage";
import PatternTemplatesPage from "@/pages/PatternTemplatesPage";
import PatternSpecificationsPage from "@/pages/PatternSpecificationsPage";
import UsersPage from "@/pages/UsersPage";
import AddressBookPage from "@/pages/AddressBookPage";
import PermissionsPage from "@/pages/PermissionsPage";
import KioskRegisterPage from "@/pages/KioskRegisterPage";
import StoresPage from "@/pages/StoresPage";
import FactoriesPage from "@/pages/FactoriesPage";
import BackupManagementPage from "@/pages/BackupManagementPage";
import StorageCleanupPage from "@/pages/StorageCleanupPage";
import GoogleSheetSyncPage from "@/pages/GoogleSheetSyncPage";
import MeasurementImportPriorityPage from "@/pages/MeasurementImportPriorityPage";
import EmailSettingsPage from "@/pages/EmailSettingsPage";
import IntercompanyCostingPage from "@/pages/IntercompanyCostingPage";
import MuslinInventoryPage from "@/pages/MuslinInventoryPage";
import InventoryPage from "@/pages/InventoryPage";
import { getRoleLandingPath } from "@/lib/roleLanding";
import { useAuth } from "@/context/AuthContext";

const RoleLandingRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getRoleLandingPath(user?.role)} replace />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <CompactLayout>
                    <Routes>
                      {/* Role-based root landing */}
                      <Route path="/" element={<RoleLandingRedirect />} />
                      
                      {/* Tasks */}
                      <Route path="/tasks" element={<TasksPage />} />
                      
                      {/* Manage Ord/Enq */}
                      <Route path="/orders/dashboard" element={<OrdersDashboardPage />} />
                      <Route path="/orders" element={<OrdersPage />} />
                      <Route path="/orders/new" element={<NewOrderPage />} />
                      <Route path="/orders/:orderId/edit" element={<EditOrderPage />} />
                      
                      {/* Production */}
                      <Route path="/production-dashboard" element={<ProductionDashboardPage />} />
                      <Route path="/garments" element={<GarmentsPage />} />
                      <Route
                        path="/muslin-inventory"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'staff', 'production']}>
                            <MuslinInventoryPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inventory"
                        element={
                          <ProtectedRoute
                            allowedRoles={['owner', 'admin', 'production', 'manager']}
                            pagePermission="inventory"
                            pagePermissionRoles={['manager']}
                          >
                            <InventoryPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/fabric-procurement" element={<FabricProcurementPage />} />
                      <Route path="/fabric-orders" element={<FabricProcurementPage />} />
                      
                      {/* Customers */}
                      <Route
                        path="/customers"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'staff', 'production']}>
                            <CustomersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fitting-studio"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'staff', 'production']}>
                            <FittingStudioLandingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fitting-studio/:customerId"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'staff', 'production']}>
                            <UnifiedFittingStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/patterns/:patternId" element={<PatternDetailPage />} />
                      
                      {/* Kiosk - Kiosk role only */}
                      <Route 
                        path="/kiosk/register" 
                        element={
                          <ProtectedRoute allowedRoles={['kiosk', 'owner', 'admin']}>
                            <KioskRegisterPage />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Admin - Owner/Admin only */}
                      <Route 
                        path="/products" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager']}>
                            <ProductsPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/pattern-templates" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager']}>
                            <PatternTemplatesPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/pattern-specifications" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager']}>
                            <PatternSpecificationsPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/users" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <UsersPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/stores" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <StoresPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/factories" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <FactoriesPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/suppliers" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager']}>
                            <SuppliersPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/address-book" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager']}>
                            <AddressBookPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/permissions" 
                        element={
                          <ProtectedRoute allowedRoles={['owner']}>
                            <PermissionsPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/backup-management" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <BackupManagementPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/storage-cleanup" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <StorageCleanupPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/google-sheet-sync" 
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'production', 'staff']}>
                            <GoogleSheetSyncPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route
                        path="/measurement-import-priority"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'production', 'staff']}>
                            <MeasurementImportPriorityPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/email-settings"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <EmailSettingsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/intercompany-costing"
                        element={
                          <ProtectedRoute allowedRoles={['owner', 'admin']}>
                            <IntercompanyCostingPage />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* Legacy routes */}
                      <Route path="/measurements" element={<MeasurementsPage />} />
                      <Route path="/payments" element={<PaymentsPage />} />
                    </Routes>
                  </CompactLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
